import * as XLSX from 'xlsx';
import type { DslTag, TagExecState } from './types';

const DATE_COLUMN = 'Date';
const DSL_COLUMN = 'DSL';
const VALUE_COLUMN = 'Value';
const KIND_COLUMN = 'Kind';

export type ExportRowKind = 'series' | 'scalar';

export interface ExportRow {
  [DATE_COLUMN]: string;
  [DSL_COLUMN]: string;
  [VALUE_COLUMN]: number;
  [KIND_COLUMN]: ExportRowKind;
}

export interface DslChartExport {
  columns: string[];
  rows: ExportRow[];
}

const COLUMNS: string[] = [DATE_COLUMN, DSL_COLUMN, VALUE_COLUMN, KIND_COLUMN];

export const buildChartExport = (
  tags: DslTag[],
  execState: Record<string, TagExecState>,
): DslChartExport => {
  const rows: ExportRow[] = [];
  for (const tag of tags) {
    const state = execState[tag.id];
    if (!state || state.status !== 'ok') continue;
    const result = state.result;
    if (!result) continue;
    if (result.type === 'time_series') {
      const length = Math.min(result.dates.length, result.values.length);
      for (let i = 0; i < length; i += 1) {
        const value = result.values[i];
        if (value === null || value === undefined || !Number.isFinite(value)) continue;
        rows.push({
          [DATE_COLUMN]: result.dates[i],
          [DSL_COLUMN]: tag.dsl,
          [VALUE_COLUMN]: value,
          [KIND_COLUMN]: 'series',
        });
      }
      continue;
    }
    if (result.type === 'scalar' && Number.isFinite(result.value)) {
      rows.push({
        [DATE_COLUMN]: '',
        [DSL_COLUMN]: tag.dsl,
        [VALUE_COLUMN]: result.value,
        [KIND_COLUMN]: 'scalar',
      });
    }
  }
  return { columns: COLUMNS, rows };
};

export const hasChartExportData = (
  tags: DslTag[],
  execState: Record<string, TagExecState>,
): boolean => buildChartExport(tags, execState).rows.length > 0;

export const downloadChartAsExcel = (
  tags: DslTag[],
  execState: Record<string, TagExecState>,
): void => {
  const { columns, rows } = buildChartExport(tags, execState);
  if (rows.length === 0) return;

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(rows, { header: columns });
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Chart Data');

  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  XLSX.writeFile(workbook, `dsl_chart_${timestamp}.xlsx`);
};
